import { create } from 'zustand';

// Mode embed (iframe Arsenal/BPM) : activé via (1) la variable d'environnement VITE_FORCE_EMBED,
// (2) le paramètre URL `?embed=1`/`embed=true`, ou (3) la sessionStorage persistée pour la session.
// Persisté en `sessionStorage` pour survivre à la redirection OIDC vers le fournisseur externe.
const EMBED_SESSION_KEY = 'dp_embed_mode';

const FORCE_EMBED = (() => {
  try {
    // import.meta.env est fourni par Vite. La valeur attendue est la string 'true'.
    // @ts-ignore
    return typeof import.meta !== 'undefined' && import.meta.env?.VITE_FORCE_EMBED === 'true';
  } catch {
    return false;
  }
})();

function detectEmbed(): boolean {
  try {
    if (FORCE_EMBED) {
      try { sessionStorage.setItem(EMBED_SESSION_KEY, 'true'); } catch {}
      return true;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('embed') === '1' || params.get('embed') === 'true') {
      sessionStorage.setItem(EMBED_SESSION_KEY, 'true');
      return true;
    }

    // Tenant TSLH : toujours en mode embed (pas de navbar), même sans `?embed=1`
    // et même après un retour de connexion sans le paramètre dans l'URL.
    if (params.get('tslh') === '1' || params.get('tslh') === 'true' || sessionStorage.getItem('dp_tenant') === 'tslh') {
      sessionStorage.setItem(EMBED_SESSION_KEY, 'true');
      return true;
    }

    // Fallback robuste: si l'app est chargée dans une iframe, on force le mode embed
    // même si le paramètre URL ou le sessionStorage ont été perdus entre deux visites.
    if (window.self !== window.top) {
      try { sessionStorage.setItem(EMBED_SESSION_KEY, 'true'); } catch {}
      return true;
    }

    return sessionStorage.getItem(EMBED_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

// Apply `embed-mode` class on <html> so CSS can scope embed-only overrides
// without touching the normal mode. Runs once at module load.
const _isEmbed = detectEmbed();
if (_isEmbed && typeof document !== 'undefined') {
  document.documentElement.classList.add('embed-mode');
}

interface EmbedState {
  isEmbed: boolean;
}

export const useEmbedStore = create<EmbedState>(() => ({
  isEmbed: _isEmbed,
}));

export function getEmbedAwarePath(path: string): string {
  if (!_isEmbed) return path;

  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('embed', '1');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path.includes('?') ? `${path}&embed=1` : `${path}?embed=1`;
  }
}
